import * as React from "react"
import { toast as sonnerToast } from "sonner"

type ToastVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | (string & {})

type ToastId = string | number

type SonnerAction = {
  label: string
  onClick: () => void
}

type ToastInput = {
  id?: ToastId
  title?: React.ReactNode
  description?: React.ReactNode
  duration?: number
  action?: unknown
  variant?: ToastVariant
  [key: string]: unknown
}

type ToasterToast = ToastInput & { id: ToastId }

type Toast = ToastInput

const EMPTY_TOASTS: ToasterToast[] = []

const resolveMessage = (input: Toast) => {
  if (input.title) return input.title
  if (input.description) return input.description
  return "Aviso"
}

const resolveDescription = (input: Toast) => {
  if (input.title) return input.description
  return undefined
}

const resolveAction = (action: Toast["action"]) => {
  if (!action || typeof action !== "object") {
    return undefined
  }

  const candidate = action as Partial<SonnerAction>
  if (typeof candidate.label === "string" && typeof candidate.onClick === "function") {
    return candidate as SonnerAction
  }

  return undefined
}

const resolveId = (input: Toast, id?: ToastId) => {
  if (id !== undefined) return id
  if (typeof input.id === "string" || typeof input.id === "number") return input.id
  return undefined
}

const emitToast = (input: Toast, id?: ToastId) => {
  const message = resolveMessage(input)
  const description = resolveDescription(input)
  const action = resolveAction(input.action)
  const resolvedId = resolveId(input, id)

  const options = {
    description,
    duration: input.duration,
    id: resolvedId,
    action,
  }

  if (input.variant === "destructive") {
    return sonnerToast.error(message, options)
  }

  return sonnerToast(message, options)
}

function toast({ ...props }: Toast) {
  const id = emitToast(props)

  const update = (next: Toast) => emitToast({ ...props, ...next }, id)
  const dismiss = () => sonnerToast.dismiss(id)

  return {
    id,
    dismiss,
    update,
  }
}

function useToast() {
  return {
    toast,
    dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
    toasts: EMPTY_TOASTS,
  }
}

export { useToast, toast }
